/**
 * @file Слой 0 - Внутреннее пространство сферы
 * @module membrane/src/layers/Layer0Sphere
 * 
 * Пользователь оказывается внутри полупрозрачной сферической формы.
 * Граница сферы — как стекло, на котором почти прозрачные тексты, образы, формы и смыслы.
 * Снаружи видна та же туманность, из которой был вход.
 * 
 * Поведение: сфера движется сама, если пользователь неподвижен.
 */

import { motion, useMotionTemplate, useMotionValue } from 'framer-motion';
import { BaseLayerProps } from '../types/membrane';
import { useState, useEffect, useRef } from 'react';
import { getSphere, getGallery, submitFeedback } from '../services/mirrorApi';
import { translateElementText, preloadSphereNames, parseSphereIdToReadableName } from '../services/sphereNameTranslator';

interface Layer0SphereProps extends BaseLayerProps {
  sphereId?: string;
}

/**
 * Компонент Layer0Sphere - внутреннее пространство сферы
 * 
 * Пользователь внутри сферы:
 * - Граница сферы как прозрачное стекло с почти прозрачными текстами
 * - Снаружи видна туманность
 * - Если нет движения мышки - сфера движется сама произвольно
 * - Скролл приближает/отдаляет к границе (максимальное отдаление - центр)
 * - Движение мышки - навигация по поверхности сферы
 * - Клик - первичная кристаллизация образа
 * - Вторичный клик - разворачивание образа/текста
 */
export default function Layer0Sphere({ onReturn, state: _state, sphereId = '1' }: Layer0SphereProps) {
  const [isMoving, setIsMoving] = useState(false);
  const [scrollDistance, setScrollDistance] = useState(0); // 0 = центр, 1 = граница
  const [sphereSizeScale, setSphereSizeScale] = useState(2.30);
  const [innerBlur, setInnerBlur] = useState(8);
  const [outerBlur, setOuterBlur] = useState(15);
  const [selectedElement, setSelectedElement] = useState<number | null>(null);
  // Параметры видимости элементов
  const [elementOpacity, setElementOpacity] = useState(0.90);
  const [elementSelectedOpacity, setElementSelectedOpacity] = useState(0.90);
  const [elementGlowIntensity, setElementGlowIntensity] = useState(0.00);
  const [elementTextSize, setElementTextSize] = useState(17);
  const [elementTextColor, setElementTextColor] = useState('#044d71'); // Цвет текста
  const [sphereElements, setSphereElements] = useState<Array<{ id: number; text: string; angle: number; type: string; content?: string; connectionInfo?: any }>>([]);
  const [translatedElements, setTranslatedElements] = useState<Map<number, string>>(new Map());
  const [currentSphereParsed, setCurrentSphereParsed] = useState<{ fullName: string } | null>(null);
  const [elementConnectionInfo, setElementConnectionInfo] = useState<Map<number, { relationKind: string; relationKindTranslated: string; connectedSphereId: string; connectedSphereName: string }>>(new Map());
  const [_loading, setLoading] = useState(true);
  // Состояния для модальных окон
  const [showGallery, setShowGallery] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastMouseMove = useRef(Date.now());
  const autoMoveDirection = useRef({ rotationX: 0, rotationY: 0 });
  const autoMoveTimer = useRef<number | null>(null);
  const [, setViewportSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  // Отслеживание размера viewport
  useEffect(() => {
    const handleResize = () => {
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Вращение сферы (только вращение, без движения по X/Y)
  const sphereRotationX = useMotionValue(0);
  const sphereRotationY = useMotionValue(0);

  // Blur mask с фиксированным центром в 50% 50% (радиус = половина диаметра сферы)
  const blurMask = useMotionTemplate`radial-gradient(circle calc(20vw * ${sphereSizeScale}) at 50% 50%, transparent 0%, transparent calc(20vw * ${sphereSizeScale}), black calc(20vw * ${sphereSizeScale} + 1px))`;

  // Автоматическое движение сферы при неподвижности (произвольное направление и интервал)
  useEffect(() => {
    const startAutoMove = () => {
      if (autoMoveTimer.current) {
        window.clearTimeout(autoMoveTimer.current);
      }

      // Генерируем новое произвольное направление вращения (еле заметное)
      autoMoveDirection.current = {
        rotationX: (Math.random() - 0.5) * 3,
        rotationY: (Math.random() - 0.5) * 3,
      };

      // Произвольный интервал до следующего изменения направления (2-8 секунд)
      const nextChangeInterval = 2000 + Math.random() * 6000;

      autoMoveTimer.current = window.setTimeout(() => {
        if (!isMoving) {
          startAutoMove();
        }
      }, nextChangeInterval);
    };

    if (!isMoving) {
      startAutoMove();
    }

    return () => {
      if (autoMoveTimer.current) {
        window.clearTimeout(autoMoveTimer.current);
      }
    };
  }, [isMoving]);

  // Применение автоматического вращения
  useEffect(() => {
    if (!isMoving) {
      const interval = setInterval(() => {
        const time = Date.now();
        const dir = autoMoveDirection.current;
        
        // Медленное, еле заметное вращение
        sphereRotationX.set(Math.sin(time / 5000) * dir.rotationX);
        sphereRotationY.set(Math.cos(time / 6000) * dir.rotationY);
      }, 50);

      return () => clearInterval(interval);
    }
  }, [isMoving, sphereRotationX, sphereRotationY]);

  // Отслеживание движения мыши
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setIsMoving(true);
      lastMouseMove.current = Date.now();

      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const xPos = ((e.clientX - rect.left) / rect.width) * 100;
        const yPos = ((e.clientY - rect.top) / rect.height) * 100;
        // Центр фиксирован, реагирует только вращение (еле заметное)
        sphereRotationX.set(Math.max(-3, Math.min(7, (yPos - 50) * 0.1)));
        sphereRotationY.set(Math.max(-3, Math.min(7, (xPos - 50) * 0.1)));
      }

      // Сброс флага неподвижности через 2 секунды
      setTimeout(() => {
        if (Date.now() - lastMouseMove.current > 2000) {
          setIsMoving(false);
        }
      }, 2000);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [sphereRotationX, sphereRotationY]);

  // Обработка скролла (приближение/отдаление к границе, максимальное отдаление - центр)
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      setScrollDistance((prev) => {
        const newDistance = prev + e.deltaY * 0.01;
        return Math.max(0, Math.min(1, newDistance)); // 0 = центр, 1 = граница
      });
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, []);

  // Загрузка данных сферы через Mirror API
  useEffect(() => {
    const loadSphereData = async () => {
      try {
        setLoading(true);
        const sphereData = await getSphere(sphereId);
        console.log('Loaded sphere data:', sphereData);
        console.log('First element details:', sphereData.elements?.[0]);
        const elements = sphereData.elements || [];
        console.log('Sphere elements:', elements);
        if (elements.length === 0) {
          // Fallback к дефолтным элементам, если API не вернул данные
          console.log('No elements from API, using fallback');
          setSphereElements([
            { id: 1, text: 'Пространства', angle: 0, type: 'text' },
            { id: 2, text: 'Сущности', angle: 72, type: 'text' },
            { id: 3, text: 'Многомерность', angle: 144, type: 'text' },
            { id: 4, text: 'Начало', angle: 216, type: 'text' },
            { id: 5, text: 'Поле', angle: 288, type: 'text' },
          ]);
        } else {
          // Добавляем описание сферы как отдельный элемент, если оно есть
          const firstElement = elements[0];
          // Пробуем получить описание из разных источников:
          // 1. Из content первого элемента (если он отличается от text)
          // 2. Из description в sphereData (если есть)
          // 3. Из note_ru в sphereData (если есть)
          // Важно: проверяем, что content действительно отличается от text
          let sphereDescription = '';
          
          // Проверяем content первого элемента
          if (firstElement?.content && 
              firstElement.content.trim() !== '' && 
              firstElement.content !== firstElement?.text &&
              firstElement.content.length > 20) {
            sphereDescription = firstElement.content;
          } else if ((sphereData as any).description) {
            sphereDescription = (sphereData as any).description;
          } else if ((sphereData as any).note_ru) {
            sphereDescription = (sphereData as any).note_ru;
          }
          
          console.log('Checking for description:', {
            firstElementText: firstElement?.text,
            firstElementContent: firstElement?.content,
            firstElementContentLength: firstElement?.content?.length || 0,
            firstElementContentDiffers: firstElement?.content !== firstElement?.text,
            sphereDataDescription: (sphereData as any).description,
            sphereDataNoteRu: (sphereData as any).note_ru,
            sphereDescription,
            hasDescription: !!sphereDescription,
            descriptionLength: sphereDescription.length,
          });
          
          // Если есть описание и оно отличается от названия, добавляем его как отдельный элемент
          if (sphereDescription && sphereDescription.trim() !== '' && sphereDescription !== firstElement?.text && sphereDescription.length > 20) {
            // Находим максимальный ID среди существующих элементов
            const maxId = Math.max(...elements.map((e: { id: number; text: string; angle: number; type: string; content?: string }) => e.id || 0), 0);
            
            // Вставляем описание как второй элемент (после названия)
            const descriptionElement = {
              id: maxId + 1, // Уникальный ID для описания
              text: 'Описание',
              angle: (firstElement?.angle || 0) + 30, // Смещаем немного от названия
              type: 'text',
              content: sphereDescription,
            };
            elements.splice(1, 0, descriptionElement); // Вставляем после первого элемента
            console.log('Added description element:', descriptionElement);
          } else {
            console.log('Description not added:', {
              reason: !sphereDescription ? 'no description' : 
                      sphereDescription === firstElement?.text ? 'same as title' : 
                      sphereDescription.length <= 10 ? 'too short' :
                      'unknown',
              sphereDescription: sphereDescription.substring(0, 50),
            });
          }
          
          setSphereElements(elements);
          console.log('Processing elements for sphere:', sphereId, 'elements count:', elements.length);
          // Транслируем названия элементов
          const translations = new Map<number, string>();
          const connectionInfoMap = new Map<number, { relationKind: string; relationKindTranslated: string; connectedSphereId: string; connectedSphereName: string }>();
          
          // Предзагружаем названия связанных сфер
          if (sphereData.connections && sphereData.connections.length > 0) {
            await preloadSphereNames(sphereData.connections);
          }
          
          await Promise.all(
            elements.map(async (element: { id: number; text: string; angle: number; type: string; content?: string }, index: number) => {
              const currentLang = 'ru'; // TODO: получить из настроек пользователя по региону
              
              // Первый элемент (id === 1) - это название сферы, добавляем префикс типа
              if (element.id === 1 || index === 0) {
                
                // Используем новую логику парсинга ID по частям
                // Это гарантирует правильный перевод независимо от региона
                const parsed = parseSphereIdToReadableName(
                  sphereId, 
                  currentLang, 
                  sphereData.name_ru // Используем name_ru как самый надежный источник
                );
                
                console.log(`Parsed sphere name for ${sphereId}:`, parsed);
                
                // Сохраняем информацию о текущей сфере для использования в модальном окне
                setCurrentSphereParsed(parsed);
                
                // Используем полное название из парсера
                const nameWithPrefix = parsed.fullName;
                console.log(`Final name with prefix: "${nameWithPrefix}"`);
                
                translations.set(element.id, nameWithPrefix);
              } else if (element.text === 'Описание' && element.content) {
                // Элемент с описанием сферы - показываем короткую версию или метку
                // Полное описание будет в модальном окне при клике
                const shortDescription = element.content.length > 60 
                  ? element.content.substring(0, 60) + '...' 
                  : element.content;
                translations.set(element.id, shortDescription);
                console.log(`Description element (id=${element.id}): "${shortDescription}"`);
              } else {
                // Остальные элементы - это связи с другими сферами
                // Сопоставляем элемент с соответствующей связанной сферой по порядку
                // Учитываем, что первый элемент (index 0) - название сферы, 
                // второй (index 1) может быть описанием, поэтому начинаем с index - 1 или -2
                let connectionIndex = index - 1; // По умолчанию вычитаем 1 для названия
                
                // Если второй элемент - это описание, то связи начинаются с index - 2
                if (elements[1]?.text === 'Описание') {
                  connectionIndex = index - 2;
                }
                
                if (connectionIndex >= 0 && sphereData.connections && connectionIndex < sphereData.connections.length) {
                  const connectedSphereId = sphereData.connections[connectionIndex];
                  
                  // Загружаем данные связанной сферы для получения name_ru
                  let connectedSphereName = '';
                  try {
                    const connectedSphereData = await getSphere(connectedSphereId);
                    // Используем parseSphereIdToReadableName для правильного перевода
                    const parsedConnected = parseSphereIdToReadableName(
                      connectedSphereId,
                      currentLang,
                      connectedSphereData.name_ru
                    );
                    connectedSphereName = parsedConnected.fullName;
                  } catch (error) {
                    console.warn(`Failed to load connected sphere ${connectedSphereId}:`, error);
                    // Fallback: используем только парсинг ID без загрузки данных
                    const parsedConnected = parseSphereIdToReadableName(connectedSphereId, currentLang);
                    connectedSphereName = parsedConnected.fullName;
                  }
                  
                  // Переводим relation kind (название связи)
                  const relationKindTranslated = await translateElementText(element.text);
                  
                  // Форматируем как "название связи: название сферы"
                  const connectionLabel = `${relationKindTranslated}: ${connectedSphereName}`;
                  
                  console.log(`Element ${element.id} (${element.text}): connected to "${connectedSphereId}" -> "${connectionLabel}"`);
                  translations.set(element.id, connectionLabel);
                  
                  // Сохраняем информацию о связи для модального окна
                  connectionInfoMap.set(element.id, {
                    relationKind: element.text,
                    relationKindTranslated,
                    connectedSphereId,
                    connectedSphereName,
                  });
                } else {
                  // Если связи нет, переводим relation kind как fallback
                  const translated = await translateElementText(element.text);
                  console.log(`Element ${element.id}: "${element.text}" -> "${translated}" (no connection)`);
                  translations.set(element.id, translated);
                }
              }
            })
          );
          setTranslatedElements(translations);
          setElementConnectionInfo(connectionInfoMap);
        }
      } catch (error) {
        console.error('Error loading sphere data:', error);
        // Fallback к дефолтным элементам
        setSphereElements([
          { id: 1, text: 'Пространства', angle: 0, type: 'text' },
          { id: 2, text: 'Сущности', angle: 72, type: 'text' },
          { id: 3, text: 'Многомерность', angle: 144, type: 'text' },
          { id: 4, text: 'Начало', angle: 216, type: 'text' },
          { id: 5, text: 'Поле', angle: 288, type: 'text' },
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadSphereData();
  }, [sphereId]);

  // Отладка: логируем количество элементов
  useEffect(() => {
    if (sphereElements.length > 0) {
      console.log(`Sphere elements count: ${sphereElements.length}`, sphereElements);
    }
  }, [sphereElements]);

  // Обработка клавиши Esc для возврата
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Если открыта галерея - закрываем её
        if (showGallery) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          setShowGallery(false);
          return;
        }
        // Если открыта форма регистрации - закрываем её
        if (showRegistrationForm) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          setShowRegistrationForm(false);
          return;
        }
        // Если выбран элемент - закрываем его
        if (selectedElement !== null) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          setSelectedElement(null);
          return;
        }
        // Иначе возвращаемся на предыдущую страницу
        if (onReturn) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          onReturn();
        }
      }
    };

    // Используем capture phase, чтобы перехватить событие раньше глобального обработчика
    window.addEventListener('keydown', handleEscape, true);
    return () => window.removeEventListener('keydown', handleEscape, true);
  }, [showGallery, showRegistrationForm, selectedElement, onReturn]);

  // Обработка клика по элементу - показываем содержимое в центре
  const handleElementClick = (elementId: number) => {
    // Если элемент уже выбран, закрываем его
    if (selectedElement === elementId) {
      setSelectedElement(null);
    } else {
      // Иначе показываем содержимое в центре
      setSelectedElement(elementId);
    }
  };

  // Вычисление позиции элемента на сфере с учетом границ экрана
  const getElementPosition = (angle: number, distance: number, elementText: string = '', textSize: number = 14) => {
    const angleRad = (angle * Math.PI) / 180;
    const maxRadius = 45; // Максимальный радиус в процентах
    const minRadius = 25; // Минимальный радиус (ближе к центру)
    const radius = minRadius + (maxRadius - minRadius) * distance;
    
    // Вычисляем базовую позицию
    let xPos = 50 + Math.cos(angleRad) * radius;
    let yPos = 50 + Math.sin(angleRad) * radius;
    
    // Оцениваем размер элемента на основе текста
    const charWidth = textSize * 0.6;
    const estimatedWidth = Math.max(100, elementText.length * charWidth + 40);
    const estimatedHeight = textSize * 2.5;
    
    // Получаем размеры контейнера
    if (containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;
      
      // Вычисляем абсолютные координаты элемента (центр элемента)
      const elementLeft = (xPos / 100) * containerWidth;
      const elementTop = (yPos / 100) * containerHeight;
      
      // Вычисляем половину размера элемента в пикселях
      const halfWidth = estimatedWidth / 2;
      const halfHeight = estimatedHeight / 2;
      
      // Проверяем границы и корректируем позицию
      const padding = 15; // Отступ от края в пикселях
      
      // Левая граница
      if (elementLeft - halfWidth < padding) {
        const offsetX = ((padding + halfWidth - elementLeft) / containerWidth) * 100;
        xPos = Math.max(padding / containerWidth * 100, xPos + offsetX);
      }
      
      // Правая граница
      if (elementLeft + halfWidth > containerWidth - padding) {
        const offsetX = ((elementLeft + halfWidth - containerWidth + padding) / containerWidth) * 100;
        xPos = Math.min((containerWidth - padding) / containerWidth * 100, xPos - offsetX);
      }
      
      // Верхняя граница
      if (elementTop - halfHeight < padding) {
        const offsetY = ((padding + halfHeight - elementTop) / containerHeight) * 100;
        yPos = Math.max(padding / containerHeight * 100, yPos + offsetY);
      }
      
      // Нижняя граница
      if (elementTop + halfHeight > containerHeight - padding) {
        const offsetY = ((elementTop + halfHeight - containerHeight + padding) / containerHeight) * 100;
        yPos = Math.min((containerHeight - padding) / containerHeight * 100, yPos - offsetY);
      }
      
      // Ограничиваем значения в пределах разумных границ
      xPos = Math.max(5, Math.min(95, xPos));
      yPos = Math.max(5, Math.min(95, yPos));
    }
    
    return { x: xPos, y: yPos };
  };

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.5, ease: 'easeInOut' }}
      className="absolute inset-0 w-full h-full overflow-hidden"
      style={{
        backgroundImage:
          "url('/reveal_bg_clean.png'), url('/reveal_bg.svg'), radial-gradient(ellipse at center, hsl(220, 50%, 92%) 0%, hsl(240, 45%, 87%) 50%, hsl(260, 40%, 82%) 100%)",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at center, rgba(255,255,255,0.35) 0%, rgba(245,245,255,0.12) 45%, rgba(210,220,245,0.08) 70%, rgba(120,130,180,0.0) 100%)',
          mixBlendMode: 'screen',
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at center, rgba(255,255,255,0) 62%, rgba(180,200,235,0.12) 78%, rgba(120,140,200,0.22) 100%)',
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 8% 8%, rgba(210,230,255,0.45) 0%, rgba(210,230,255,0.18) 30%, rgba(210,230,255,0.0) 60%), radial-gradient(circle at 92% 8%, rgba(210,230,255,0.45) 0%, rgba(210,230,255,0.18) 30%, rgba(210,230,255,0.0) 60%), radial-gradient(circle at 8% 92%, rgba(210,230,255,0.45) 0%, rgba(210,230,255,0.18) 30%, rgba(210,230,255,0.0) 60%), radial-gradient(circle at 92% 92%, rgba(210,230,255,0.45) 0%, rgba(210,230,255,0.18) 30%, rgba(210,230,255,0.0) 60%)',
          mixBlendMode: 'screen',
          opacity: 0.8,
        }}
      />
      {/* Туманность снаружи (видна через сферу) - наполненная энергия */}
      <motion.div
        className="absolute inset-0 opacity-50"
        style={{
          background:
            'radial-gradient(circle at 35% 35%, rgba(255,255,255,0.18) 0%, rgba(200,215,255,0.05) 45%, rgba(120,140,200,0.0) 80%)',
        }}
      />

      {/* Blur mask для внешнего размытия - применяется ко всему экрану */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          backdropFilter: `blur(${outerBlur}px)`,
          WebkitMaskImage: blurMask,
          maskImage: blurMask,
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
        }}
      />

      {/* Сферическая форма (прозрачное стекло) - фиксированный центр */}
      <div
        className="absolute"
        style={{
          left: '50%',
          top: '50%',
          width: `calc(40vw * ${sphereSizeScale})`,
          height: `calc(40vw * ${sphereSizeScale})`,
          transform: 'translate(-50%, -50%)',
          transformOrigin: '50% 50%',
        }}
      >
        <motion.div
          className="absolute inset-0 rounded-full border-2"
          style={{
            rotateX: sphereRotationX,
            rotateY: sphereRotationY,
            transformStyle: 'preserve-3d',
            transformOrigin: '50% 50%',
            borderColor: 'rgba(210, 230, 255, 0.22)',
            background: 'transparent',
            backdropFilter: `blur(${innerBlur}px) saturate(1.1)`,
            boxShadow: 'inset 0 0 120px rgba(255, 255, 255, 0.08)',
          }}
        >
        <div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            boxShadow: 'inset 0 0 25px rgba(255,255,255,0.25), inset 0 0 80px rgba(140,170,220,0.15)',
            background:
              'radial-gradient(circle at 40% 35%, rgba(255,255,255,0.12) 0%, rgba(180,210,255,0.06) 35%, rgba(120,150,210,0.0) 70%)',
            mixBlendMode: 'screen',
          }}
        />
        <div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            border: '1px solid rgba(210,230,255,0.25)',
            filter: `blur(${outerBlur}px)`,
            opacity: 0.65,
          }}
        />
        <div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 0 24px rgba(180,210,255,0.3)',
          }}
        />
        {/* Элементы на границе сферы (постепенно проявляющиеся блоки) */}
        {sphereElements.length > 0 && (
          <>
            {sphereElements.map((element, index) => {
              const elementText = translatedElements.get(element.id) || element.text || '';
              const position = getElementPosition(element.angle, scrollDistance, elementText, elementTextSize);
              // Задержка для постепенного появления: каждый элемент появляется с небольшой задержкой
              const appearDelay = index * 0.15; // 150ms между каждым элементом
              
              // Логируем элементы описания для отладки
              if (element.text === 'Описание') {
                console.log('Rendering description element:', {
                  id: element.id,
                  text: element.text,
                  angle: element.angle,
                  position,
                  translatedText: translatedElements.get(element.id),
                });
              }

              return (
                <motion.div
                  key={element.id}
                  className="absolute cursor-pointer"
                  style={{
                    left: `${position.x}%`,
                    top: `${position.y}%`,
                    transform: 'translate(-50%, -50%)',
                    zIndex: 9999,
                    pointerEvents: 'auto',
                  }}
                  initial={{ 
                    opacity: 0,
                    scale: 0.8,
                    y: 10,
                  }}
                  animate={{
                    opacity: selectedElement === element.id ? 0.9 : 0.3,
                    scale: selectedElement === element.id ? 1.15 : 1,
                    y: 0,
                  }}
                  whileHover={{
                    opacity: 0.7,
                    scale: 1.1,
                  }}
                  transition={{ 
                    duration: 0.6,
                    delay: appearDelay,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                  onClick={() => handleElementClick(element.id)}
                >
                  {/* Красивый полупрозрачный элемент с эффектами */}
                  <div
                    className="whitespace-nowrap relative"
                    style={{
                      fontSize: `${elementTextSize}px`,
                      fontWeight: 500, // Средняя толщина для лучшей читабельности
                      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif", // Системные шрифты для всех устройств
                      opacity: selectedElement === element.id ? elementSelectedOpacity : elementOpacity,
                      color: elementTextColor,
                      WebkitTextStroke: '1px rgba(180, 220, 255, 0.95)',
                      paintOrder: 'stroke fill',
                      textShadow: selectedElement === element.id
                        ? `0 0 ${8 * elementGlowIntensity}px rgba(200, 220, 255, ${0.8 * elementGlowIntensity})`
                        : `0 0 ${4 * elementGlowIntensity}px rgba(200, 220, 255, ${0.5 * elementGlowIntensity})`,
                      padding: '8px 14px',
                      borderRadius: '8px',
                      background: selectedElement === element.id
                        ? `radial-gradient(circle at center, rgba(255, 243, 24, 0.1) 0%, rgba(255, 243, 24, 0.05) 40%, rgba(255, 243, 24, 0.01) 70%, rgba(0, 0, 0, 0) 100%)`
                        : `radial-gradient(circle at center, rgba(22, 61, 255, 0.25) 0%, rgba(22, 61, 255, 0.2) 40%, rgba(22, 61, 255, 0.05) 70%, rgba(0, 0, 0, 0) 100%)`,
                      border: selectedElement === element.id
                        ? `1.5px solid rgba(200, 220, 255, 0.6)`  
                        : `1px solid rgba(200, 220, 255, 0.4)`,
                      backdropFilter: 'blur(6px)',
                      transition: 'all 0.3s ease',
                      boxShadow: selectedElement === element.id
                        ? '0 0 20px rgba(200, 220, 255, 0.3)'
                        : '0 0 10px rgba(200, 220, 255, 0.15)',
                    }}
                  >
                    {translatedElements.get(element.id) || element.text || `Element ${element.id}`}
                    {/* Легкое свечение вокруг выбранного элемента */}
                    {selectedElement === element.id && (
                      <motion.div
                        className="absolute inset-0 rounded-lg pointer-events-none"
                        style={{
                          boxShadow: '0 0 20px rgba(200, 220, 255, 0.4), inset 0 0 10px rgba(255, 255, 255, 0.1)',
                        }}
                        animate={{
                          opacity: [0.6, 1, 0.6],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      />
                    )}
                  </div>
                </motion.div>
              );
            })}
          </>
        )}
        </motion.div>
      </div>

      {/* Модальное окно с содержимым выбранного элемента в центре */}
      {selectedElement !== null && (() => {
        const element = sphereElements.find(e => e.id === selectedElement);
        if (!element) return null;
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 flex items-center justify-center z-[10000] pointer-events-none"
            onClick={() => setSelectedElement(null)}
          >
            <motion.div
              className="bg-white/10 backdrop-blur-sm border border-blue-3 rounded-lg p-8 max-w-2xl max-h-[80vh] overflow-y-auto pointer-events-auto cursor-pointer shadow-2xl"
              style={{
                boxShadow: '0 0 40px rgba(136, 196, 255, 0.2), 0 20px 60px rgba(136, 196, 255, 0.15)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-blue-900 text-2xl font-light">{translatedElements.get(element.id) || element.text}</h2>
                <button
                  onClick={() => setSelectedElement(null)}
                  className="text-blue-600 hover:text-blue-900 transition-colors"
                  aria-label="Закрыть"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {/* Если это связь, показываем описание связи */}
              {elementConnectionInfo.has(element.id) ? (
                <div className="text-blue-700 text-sm font-light leading-relaxed">
                  <p className="mb-2">
                    <span className="font-medium">{currentSphereParsed?.fullName || sphereId}</span> имеет связь{' '}
                    <span className="font-medium">{elementConnectionInfo.get(element.id)?.relationKindTranslated}</span> с{' '}
                    <span className="font-medium">{elementConnectionInfo.get(element.id)?.connectedSphereName}</span>.
                  </p>
                  {element.content && (
                    <div className="mt-4 pt-4 border-t border-blue-200/30">
                      <p className="text-blue-600 italic whitespace-pre-wrap">{element.content}</p>
                    </div>
                  )}
                </div>
              ) : element.content ? (
                <div className="text-blue-700 text-sm font-light leading-relaxed whitespace-pre-wrap">
                  {element.content}
                </div>
              ) : (
                <div className="text-blue-500 text-sm font-light">
                  Содержимое элемента будет здесь.
                </div>
              )}
              
              {/* Кнопки действий внизу модального окна */}
              <div className="mt-6 pt-6 border-t border-blue-200/30 flex gap-4">
                <button
                  onClick={() => setShowGallery(true)}
                  className="flex-1 px-4 py-3 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/50 text-blue-700 rounded-lg transition-all font-medium text-sm backdrop-blur-sm hover:shadow-lg"
                >
                  Галерея
                </button>
                <button
                  onClick={() => setShowRegistrationForm(true)}
                  className="flex-1 px-4 py-3 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400/50 text-purple-700 rounded-lg transition-all font-medium text-sm backdrop-blur-sm hover:shadow-lg"
                >
                  Нырнуть в глубину
                </button>
              </div>
            </motion.div>
          </motion.div>
        );
      })()}

      {/* Подсказка */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.3 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white/30 text-xs font-light text-center"
      >
        Скролл: приближение/отдаление • Клик: просмотр содержимого • Esc: выход
      </motion.div>

      {import.meta.env.DEV && (
      <div
        className="absolute top-6 right-6 text-white/90 text-xs z-20"
        style={{
          background: 'rgba(10, 15, 30, 0.5)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: '10px',
          padding: '10px 12px',
          backdropFilter: 'blur(6px)',
          width: '240px',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div className="text-white/70 mb-2">Сфера</div>
        {[
          {
            key: 'innerBlur',
            label: 'Blur внутри',
            min: 0,
            max: 30,
            step: 1,
            value: innerBlur,
            setter: setInnerBlur,
          },
          {
            key: 'outerBlur',
            label: 'Blur снаружи',
            min: 0,
            max: 30,
            step: 1,
            value: outerBlur,
            setter: setOuterBlur,
          },
          {
            key: 'sphereSizeScale',
            label: 'Размер сферы',
            min: 1.4,
            max: 3.0,
            step: 0.05,
            value: sphereSizeScale,
            setter: setSphereSizeScale,
          },
        ].map((item) => (
          <label key={item.key} className="block mb-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-white/70">{item.label}</span>
              <span className="text-white/50">
                {item.key === 'sphereSizeScale' ? item.value.toFixed(2) : Math.round(item.value)}
              </span>
            </div>
            <input
              type="range"
              min={item.min}
              max={item.max}
              step={item.step}
              value={item.value}
              onChange={(e) => item.setter(parseFloat(e.target.value))}
              className="w-full accent-blue-200"
            />
          </label>
        ))}
        
        {/* Разделитель */}
        <div className="border-t border-white/10 my-3"></div>
        
        {/* Параметры элементов */}
        <div className="text-white/70 mb-2">Элементы</div>
        {[
          {
            key: 'elementOpacity',
            label: 'Прозрачность',
            min: 0,
            max: 1,
            step: 0.05,
            value: elementOpacity,
            setter: setElementOpacity,
          },
          {
            key: 'elementSelectedOpacity',
            label: 'Прозрачность выбранного',
            min: 0,
            max: 1,
            step: 0.05,
            value: elementSelectedOpacity,
            setter: setElementSelectedOpacity,
          },
          {
            key: 'elementGlowIntensity',
            label: 'Интенсивность свечения',
            min: 0,
            max: 2,
            step: 0.1,
            value: elementGlowIntensity,
            setter: setElementGlowIntensity,
          },
          {
            key: 'elementTextSize',
            label: 'Размер текста',
            min: 10,
            max: 24,
            step: 1,
            value: elementTextSize,
            setter: setElementTextSize,
          },
        ].map((item) => (
          <label key={item.key} className="block mb-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-white/70">{item.label}</span>
              <span className="text-white/50">
                {item.key === 'elementTextSize' 
                  ? Math.round(item.value) 
                  : item.value.toFixed(2)}
              </span>
            </div>
            <input
              type="range"
              min={item.min}
              max={item.max}
              step={item.step}
              value={item.value}
              onChange={(e) => item.setter(parseFloat(e.target.value))}
              className="w-full accent-blue-200"
            />
          </label>
        ))}
        
        {/* Выбор цвета текста */}
        <label className="block mb-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-white/70">Цвет текста</span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={elementTextColor}
              onChange={(e) => setElementTextColor(e.target.value)}
              className="w-12 h-8 rounded cursor-pointer border border-white/20"
              style={{ backgroundColor: elementTextColor }}
            />
            <input
              type="text"
              value={elementTextColor}
              onChange={(e) => setElementTextColor(e.target.value)}
              className="flex-1 px-2 py-1 rounded text-white/90 bg-white/10 border border-white/20 text-xs"
              placeholder="#ffffff"
            />
          </div>
        </label>
      </div>
      )}

      {/* Кнопка возврата */}
      {onReturn && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          whileHover={{ opacity: 0.5 }}
          onClick={onReturn}
          className="absolute top-8 left-8 text-white/40 hover:text-white/60 transition-opacity z-10"
          aria-label="Вернуться"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
          </svg>
        </motion.button>
      )}

      {/* Модальное окно галереи */}
      {showGallery && (
        <GalleryModal onClose={() => setShowGallery(false)} />
      )}

      {/* Модальное окно формы регистрации */}
      {showRegistrationForm && (
        <RegistrationFormModal 
          onClose={() => setShowRegistrationForm(false)}
          sphereName={currentSphereParsed?.fullName || sphereId}
        />
      )}
    </motion.div>
  );
}

/**
 * Модальное окно галереи
 */
interface GalleryModalProps {
  onClose: () => void;
}

function GalleryModal({ onClose }: GalleryModalProps) {
  const [sections, setSections] = useState<Array<{ name: string; cover: string; images: string[]; count: number }>>([]);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  
  // Загружаем структуру галереи при открытии
  useEffect(() => {
    const loadGallery = async () => {
      try {
        const data = await getGallery();
        setSections(data.sections);
        if (data.sections.length > 0) {
          // По умолчанию показываем первый раздел
          setSelectedSection(data.sections[0].name);
        }
      } catch (error) {
        console.error('Error loading gallery:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadGallery();
  }, []);

  // Получаем текущие изображения выбранного раздела
  const currentSection = sections.find(s => s.name === selectedSection);
  const galleryImages = currentSection?.images || [];

  // Обработка навигации по изображениям
  const handlePreviousImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (galleryImages.length === 0) return;
    const newIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : galleryImages.length - 1;
    setSelectedImageIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  const handleNextImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (galleryImages.length === 0) return;
    const newIndex = selectedImageIndex < galleryImages.length - 1 ? selectedImageIndex + 1 : 0;
    setSelectedImageIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  // Обработка открытия изображения
  const handleImageClick = (imagePath: string, index: number) => {
    setSelectedImageIndex(index);
    setSelectedImage(imagePath);
  };

  // Обработка клавиатуры для навигации
  useEffect(() => {
    if (!selectedImage) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePreviousImage();
      } else if (e.key === 'ArrowRight') {
        handleNextImage();
      } else if (e.key === 'Escape') {
        setSelectedImage(null);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedImage, selectedImageIndex, galleryImages]);

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 flex items-center justify-center z-[20000] pointer-events-none"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white/40 backdrop-blur-md border border-blue-200/50 rounded-lg p-8 max-w-6xl max-h-[90vh] overflow-y-auto pointer-events-auto shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-blue-900 text-3xl font-light">Галерея</h2>
            <button
              onClick={onClose}
              className="text-blue-600 hover:text-blue-900 transition-colors"
              aria-label="Закрыть"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {loading ? (
            <div className="text-blue-500 text-sm italic text-center py-8">
              Загрузка галереи...
            </div>
          ) : sections.length === 0 ? (
            <div className="text-blue-500 text-sm italic text-center py-8">
              Галерея пуста. Добавьте папки с изображениями в public/gallery/
            </div>
          ) : (
            <>
              {/* Выбор раздела */}
              {sections.length > 1 && (
                <div className="mb-6 flex flex-wrap gap-3">
                  {sections.map((section) => (
                    <button
                      key={section.name}
                      onClick={() => {
                        setSelectedSection(section.name);
                        setSelectedImage(null);
                      }}
                      className={`px-4 py-2 rounded-lg transition-all ${
                        selectedSection === section.name
                          ? 'bg-blue-500 text-white shadow-lg'
                          : 'bg-white/60 text-blue-700 hover:bg-white/80'
                      }`}
                    >
                      {section.name} ({section.count})
                    </button>
                  ))}
                </div>
              )}

              {/* Сетка изображений выбранного раздела */}
              {currentSection && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {galleryImages.map((imagePath, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="relative aspect-square overflow-hidden rounded-lg cursor-pointer group"
                      onClick={() => handleImageClick(imagePath, index)}
                    >
                      <img
                        src={imagePath}
                        alt={`${currentSection.name} ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                    </motion.div>
                  ))}
                </div>
              )}
            </>
          )}
        </motion.div>
      </motion.div>

      {/* Модальное окно для просмотра изображения в полном размере */}
      {selectedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[30000] flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="relative max-w-7xl max-h-[95vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Кнопка закрытия */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white/90 hover:text-white transition-colors z-10"
              aria-label="Закрыть"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Кнопка "Назад" (влево) */}
            {galleryImages.length > 1 && (
              <button
                onClick={handlePreviousImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors z-10 bg-black/30 hover:bg-black/50 rounded-full p-3 backdrop-blur-sm"
                aria-label="Предыдущее изображение"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Изображение */}
            <img
              src={selectedImage}
              alt={`Просмотр изображения ${selectedImageIndex + 1}`}
              className="max-w-full max-h-[95vh] object-contain rounded-lg shadow-2xl mx-auto"
            />

            {/* Кнопка "Вперед" (вправо) */}
            {galleryImages.length > 1 && (
              <button
                onClick={handleNextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors z-10 bg-black/30 hover:bg-black/50 rounded-full p-3 backdrop-blur-sm"
                aria-label="Следующее изображение"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            {/* Индикатор текущего изображения */}
            {galleryImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm font-light bg-black/30 backdrop-blur-sm px-4 py-2 rounded-full">
                {selectedImageIndex + 1} / {galleryImages.length}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </>
  );
}

/**
 * Модальное окно формы регистрации для преодоления мембраны
 */
interface RegistrationFormModalProps {
  onClose: () => void;
  sphereName: string;
}

function RegistrationFormModal({ onClose, sphereName }: RegistrationFormModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    intention: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const parts = [`Сфера: ${sphereName}`];
      if (formData.phone) parts.push(`Телефон: ${formData.phone}`);
      parts.push('', `Намерение: ${formData.intention}`);
      await submitFeedback({
        name: formData.name,
        contact: formData.email,
        message: parts.join('\n'),
        sourceLayer: 0,
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error('Registration form error:', err);
      const msg = err instanceof Error ? err.message : 'Не удалось отправить. Проверьте подключение или попробуйте позже.';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 flex items-center justify-center z-[20000] pointer-events-none"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-gradient-to-br from-blue-50/95 to-purple-50/95 backdrop-blur-md border border-purple-200 rounded-lg p-8 max-w-2xl max-h-[90vh] overflow-y-auto pointer-events-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-purple-900 text-3xl font-light mb-2">Преодоление мембраны</h2>
            <p className="text-purple-600 text-sm font-light">Приглашение нырнуть в глубину мира: {sphereName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-purple-600 hover:text-purple-900 transition-colors"
            aria-label="Закрыть"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {isSubmitted ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8 px-4"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-purple-900 text-xl font-medium mb-2">Ваше сообщение успешно отправлено</h3>
            <p className="text-purple-600 text-sm">С вами свяжутся по указанным вами контактам.</p>
            <button
              onClick={onClose}
              className="mt-6 px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all font-medium"
            >
              Закрыть
            </button>
          </motion.div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-purple-800 text-sm font-medium mb-2">
              Ваше имя
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent text-purple-900 bg-white/80"
              placeholder="Как вас зовут?"
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-purple-800 text-sm font-medium mb-2">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent text-purple-900 bg-white/80"
              placeholder="your@email.com"
              required
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-purple-800 text-sm font-medium mb-2">
              Телефон
            </label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-2 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent text-purple-900 bg-white/80"
              placeholder="+882 13 XXXXXXXX"
            />
          </div>

          <div>
            <label htmlFor="intention" className="block text-purple-800 text-sm font-medium mb-2">
              Ваше намерение
            </label>
            <textarea
              id="intention"
              value={formData.intention}
              onChange={(e) => setFormData({ ...formData, intention: e.target.value })}
              className="w-full px-4 py-2 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent text-purple-900 bg-white/80 min-h-[120px] resize-y"
              placeholder="Что вас привело сюда? Что вы ищете в глубине этого мира?"
              required
            />
          </div>

          {error && (
            <p className="text-red-600 text-sm">{error}</p>
          )}
          <div className="flex gap-4 pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all font-medium shadow-lg hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Отправка...' : 'Нырнуть в глубину'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 transition-all font-medium"
            >
              Отмена
            </button>
          </div>
        </form>
        )}

        <p className="mt-6 text-purple-500 text-xs font-light text-center">
          Отправляя форму, вы соглашаетесь на обработку персональных данных
        </p>
      </motion.div>
    </motion.div>
  );
}
